import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  InputSignal,
  signal,
  TemplateRef,
  WritableSignal
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { PrimitiveTypes } from '@angular/cli/src/analytics/analytics-parameters';
import { CurrencyPipe, NgClass, NgTemplateOutlet } from '@angular/common';

@Component({
  selector: 'app-checkbox-list',
  imports: [FormsModule, CurrencyPipe, NgClass, NgTemplateOutlet],
  templateUrl: './checkbox-list.component.html',
  styleUrl: './checkbox-list.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxListComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckboxListComponent implements ControlValueAccessor {
  checkboxes: InputSignal<any[]> = input.required<any[]>();
  name: InputSignal<string> = input.required<string>();
  invalid: InputSignal<boolean> = input<boolean>(false);

  checkboxValueName: InputSignal<string> = input<string>('id'); // Empty if Need Object As Value
  checkboxLabelName: InputSignal<string> = input('label');
  maxSelectable: InputSignal<number> = input<number>(0);
  listClass: InputSignal<string> = input<string>('');
  itemClass: InputSignal<string> = input<string>('');
  priceClass: InputSignal<string> = input<string>('');
  itemTemplate: InputSignal<TemplateRef<any> | undefined> = input<
    TemplateRef<any> | undefined
  >(undefined);

  selectedValues: WritableSignal<PrimitiveTypes[]> = signal<PrimitiveTypes[]>(
    []
  );

  onValueChange(event: Event, checkboxValue: PrimitiveTypes) {
    const eventTarget: HTMLInputElement = event.target as HTMLInputElement;
    const currentSelectedValues: PrimitiveTypes[] = this.selectedValues();
    const maxSelectable = this.maxSelectable();
    const isSelected = currentSelectedValues.includes(checkboxValue);

    if (eventTarget.checked) {
      const limitReached =
        maxSelectable > 0 &&
        currentSelectedValues.length >= maxSelectable &&
        !isSelected;

      if (limitReached) {
        eventTarget.checked = false;
        eventTarget.blur();
        return;
      }

      if (!isSelected) {
        this.selectedValues.update((selectedValues: PrimitiveTypes[]) => {
          return [...selectedValues, checkboxValue];
        });
      }
    } else {
      this.selectedValues.update((selectedValues: PrimitiveTypes[]) => {
        return selectedValues.filter((item: PrimitiveTypes) => {
          return item !== checkboxValue;
        });
      });
    }

    this.onChange(this.selectedValues());
    this.onTouched();
  }

  isOptionDisabled(checkboxValue: PrimitiveTypes): boolean {
    const maxSelectable = this.maxSelectable();
    if (maxSelectable <= 0) {
      return false;
    }

    const selectedValues = this.selectedValues();
    const isSelected = selectedValues.includes(checkboxValue);

    return selectedValues.length >= maxSelectable && !isSelected;
  }

  // ControlValueAccessor
  private onChange = (value: PrimitiveTypes | PrimitiveTypes[]) => {};
  onTouched = () => {};

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(outsideValue: PrimitiveTypes[] = []): void {
    this.selectedValues.set([...(outsideValue || [])]);
  }
}
